/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { emitEvent } from "@cosmos/components";
import { getNavLocalePath } from "@cosmos/next-common";
import { useAccountCountry } from "@cosmos/next-hooks";
import { FooterLinkClickEvent } from "@wf-components/footer-consumer";
import { EpicGamesFooter } from "@wf-components/footer-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";

import { locales } from "@i18n/config";
import { LINK_OWNER } from "@site/context/link-owner";

interface Props {
	locale: string;
}

export const GlobalFooterWrapper = ({ locale }: Props): React.JSX.Element | null => {
	const router = useRouter();
	const country = useAccountCountry();
	const serverUrl = process.env.NEXT_PUBLIC_EPIC_SERVER_URL;

	const footerLinkOnClick = useCallback(
		(e: CustomEvent<FooterLinkClickEvent>) => {
			const link = e.detail?.link;

			emitEvent({
				eventCategory: "footerNav",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: link.label || "",
				eventValue: {
					component: "GlobalFooter",
					section: "footer",
					key: link.__typename === "FooterLink" ? link?.key : "",
					owner: link.owner,
					relativePath: link.relativePath,
				},
			});

			if (link && link.owner === LINK_OWNER && link.relativePath) {
				e.detail.originalEvent?.preventDefault();
				router.push(getNavLocalePath(link.relativePath, locales));
			}
		},
		[locale, router],
	);

	const searchParams = useSearchParams();
	const mode = searchParams.get("mode");
	if (mode === "game" || mode === "iosgame") {
		return null;
	}

	return (
		<EpicGamesFooter domain={serverUrl} locale={locale} countryCode={country} footerLinkOnClick={footerLinkOnClick} />
	);
};
